import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SplitText from './SplitText';

const splitTextFrom = { opacity: 0, y: 40 };
const splitTextTo = { opacity: 1, y: 0 };

const faqs = [
  {
    q: 'How long does a project take?',
    a: 'Timelines depend on the discipline. Web design: 1–2 weeks for a Starter, 3–5 for a Standard build, 6–10+ for a Premium 3D site. Motion design (which includes 3D / VFX): 1–2 weeks for a logo animation, 3–5 for a campaign piece, 6–10+ for a full 3D scene. Branding: 2–3 weeks for a Mini set, up to 7–10+ for a Comprehensive system. Logo design: 1 week for a Logo only, up to 3 for a full Logo system. Exact dates are locked in before we start.',
  },
  {
    q: "What's included after launch or handover?",
    a: 'Web projects ship with 30 days of post-launch support for bugfixes related to the original scope — completely free. For 3D, branding and logo work you get all source files plus one tweak round within 14 days of delivery. After those windows, edits are billed per hour or rolled into a small retainer (from €95/month for web maintenance).',
  },
  {
    q: 'Do you offer revisions?',
    a: 'Yes — every package has a fixed number of revision rounds baked in (1–3 depending on tier and discipline). Extra rounds or out-of-scope changes are charged at my hourly rate with a clear estimate up front. No silent scope creep on the invoice.',
  },
  {
    q: 'What does a project actually cost?',
    a: 'Indicative starting points (excl. 21% VAT): Starter website from €1.800, full custom 3D site from €7.500, Logo only from €450, Mini branding from €1.500, motion & 3D (under Motion design) from €1.800. Each quote is tailored to your scope after a short discovery call so you only pay for what you actually need.',
  },
  {
    q: 'Can I get branding or a logo without a website?',
    a: 'Absolutely. Branding and logo design are standalone offers — they are not bundled into a web build by default. Plenty of clients come in with an existing site (or none at all) and just need a sharper identity, a proper guideline or a single mark. Same goes for 3D one-offs: hero render today, no website attached.',
  },
  {
    q: 'Can you handle motion or 3D under a tight deadline?',
    a: 'Motion and 3D both live under Motion design. A static hero render in 4K is realistic in about a week if the brief is clear. Animation loops and longer hero scenes really want 3+ weeks of breathing room — light, materials and timing all need iteration. Genuinely tight deadlines (under 5 working days for renders, under 2 weeks for animation) carry a +50% rush surcharge so I can prioritise your project over everything else.',
  },
  {
    q: 'Where are you based and how do we work?',
    a: "I'm based in Belgium and work remotely with clients across Europe. Calls happen in Dutch or English, mostly via Google Meet. Once we kick off, you get a shared project board, weekly check-ins and a single contact point — me — from brief to delivery.",
  },
];

const FAQItem = ({ item, isOpen, onToggle, index }) => (
  <div className="border-b border-mainkleur/15 text-left">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`faq-panel-${index}`}
      className="w-full flex items-start justify-between gap-6 py-6 sm:py-7 lg:py-8 text-left group"
    >
      <span className="font-bricolage font-extrabold text-mainkleur text-[22px] sm:text-[28px] lg:text-[36px] leading-[1.05] tracking-tight group-hover:text-[#f97b22] transition-colors duration-300">
        {item.q}
      </span>
      <span
        className={`flex-shrink-0 mt-1 sm:mt-1.5 w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 rounded-full border border-mainkleur/30 flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-[#f97b22] border-[#f97b22] rotate-45' : 'group-hover:border-[#f97b22] group-hover:text-[#f97b22]'
        }`}
        aria-hidden="true"
      >
        <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isOpen ? 'text-mainkleur' : 'text-mainkleur'}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
        </svg>
      </span>
    </button>

    <div
      id={`faq-panel-${index}`}
      role="region"
      className={`grid overflow-hidden transition-all duration-500 ease-in-out ${
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className="min-h-0">
        <p className="font-space text-mainkleur/70 text-[15px] sm:text-[17px] lg:text-[19px] leading-[1.55] pb-6 sm:pb-7 lg:pb-8 max-w-[820px]">
          {item.a}
        </p>
      </div>
    </div>
  </div>
);

const FAQSection = ({ maxItems }) => {
  const [openIndex, setOpenIndex] = useState(0);

  // `maxItems` lets the homepage reuse this as a compact "mini FAQ" (Fase 2)
  // while /services and the full pages still render every question.
  const visibleFaqs = typeof maxItems === 'number' ? faqs.slice(0, maxItems) : faqs;

  return (
    <div className="bg-[#f2f3d9] relative py-20 sm:py-24 lg:py-32 border-t border-mainkleur/15">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-8">
        {/* Centered header */}
        <div className="flex flex-col items-center text-center gap-6 mb-12 sm:mb-16 lg:mb-20">
          <span className="font-space text-mainkleur/60 text-xs uppercase tracking-[0.22em]">
            Frequently asked
          </span>
          <h2 className="font-anton text-mainkleur text-[14vw] sm:text-[80px] lg:text-[120px] leading-[0.9] tracking-[-3px] sm:tracking-[-5px] lg:tracking-[-7px] uppercase">
            <SplitText
              text="WORKING TOGETHER"
              splitType="words"
              delay={250}
              duration={1.6}
              ease="power3.out"
              from={splitTextFrom}
              to={splitTextTo}
              threshold={0}
              rootMargin={"+100px"}
            />
          </h2>
          <p className="font-space text-mainkleur/70 text-base sm:text-lg lg:text-xl leading-[1.45] max-w-[680px]">
            Honest answers to the questions every client asks before we start. Timelines, pricing, post-launch support — all the things I'd want to know.
          </p>
        </div>

        {/* Centered accordion */}
        <div className="mx-auto">
          {visibleFaqs.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>

        {/* Footer CTA — title left, button right */}
        <div className="mt-12 sm:mt-14 lg:mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="text-left">
            <p className="font-bricolage font-normal text-mainkleur text-[20px] sm:text-[24px] lg:text-[28px] leading-[1.2] tracking-tight">
              Still have a question?
            </p>
            <span
              className="block text-mainkleur/60 font-normal text-[16px] sm:text-[18px] lg:text-[20px] mt-2"
              style={{ background: 'unset', backgroundColor: 'unset' }}
            >
              Drop me a line — I usually reply within a day.
            </span>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur rounded-full px-6 py-4 sm:px-8 sm:py-5 font-bricolage font-semibold text-lg sm:text-xl uppercase tracking-tight hover:scale-[1.03] transition-all duration-300 shadow-lg shadow-[#f97b22]/30 self-start sm:self-auto flex-shrink-0"
          >
            <span>Ask me anything</span>
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
